import { cn } from '@/lib/utils';
import { useZoneSystem } from '@/hooks/useClosedLoopAnalytics';

interface PowerZonesHudProps {
  /** User's FTP in watts. If not set, renders an "FTP not configured" placeholder. */
  ftpWatts?: number | null;
  className?: string;
}

const ZONE_COLORS: Record<number, string> = {
  1: 'hsl(200, 70%, 55%)',
  2: 'hsl(170, 80%, 45%)',
  3: 'hsl(50, 80%, 55%)',
  4: 'hsl(35, 85%, 55%)',
  5: 'hsl(15, 85%, 55%)',
  6: 'hsl(350, 80%, 55%)',
  7: 'hsl(280, 65%, 60%)',
};

/**
 * Coggan power zone model (6 zones based on % FTP).
 * Used as fallback when no persisted zones exist.
 */
function buildFallbackZones(ftp: number) {
  return [
    { id: 1, label: 'Z1 · ACTIVE RECOVERY', range: `< ${Math.round(ftp * 0.55)}w`, pct: 20, color: 'hsl(200, 70%, 55%)' },
    { id: 2, label: 'Z2 · ENDURANCE', range: `${Math.round(ftp * 0.56)}–${Math.round(ftp * 0.75)}w`, pct: 35, color: 'hsl(170, 80%, 45%)' },
    { id: 3, label: 'Z3 · TEMPO', range: `${Math.round(ftp * 0.76)}–${Math.round(ftp * 0.87)}w`, pct: 20, color: 'hsl(50, 80%, 55%)' },
    { id: 4, label: 'Z4 · THRESHOLD', range: `${Math.round(ftp * 0.88)}–${Math.round(ftp * 1.05)}w`, pct: 15, color: 'hsl(35, 85%, 55%)' },
    { id: 5, label: 'Z5 · VO2 MAX', range: `${Math.round(ftp * 1.06)}–${Math.round(ftp * 1.20)}w`, pct: 8, color: 'hsl(15, 85%, 55%)' },
    { id: 6, label: 'Z6 · ANAEROBIC', range: `> ${Math.round(ftp * 1.20)}w`, pct: 2, color: 'hsl(350, 80%, 55%)' },
  ];
}

export function PowerZonesHud({ ftpWatts, className }: PowerZonesHudProps) {
  const { data: zoneSystem, isLoading } = useZoneSystem('cycle', 'power');

  // Build zones from API data or fallback to Coggan model
  const isFromApi = !!(zoneSystem?.bands && zoneSystem.bands.length > 0);
  const effectiveFtp = isFromApi ? (zoneSystem!.anchor_value ?? ftpWatts) : ftpWatts;

  const zones = (() => {
    if (isFromApi) {
      return zoneSystem!.bands.map((band) => ({
        id: band.zone_number,
        label: `Z${band.zone_number} · ${band.name.toUpperCase()}`,
        range: `${Math.round(band.lower_bound)}–${Math.round(band.upper_bound)}w`,
        pct: Math.round(100 / zoneSystem!.bands.length),
        color: band.color ?? ZONE_COLORS[band.zone_number] ?? 'hsl(210, 30%, 50%)',
      }));
    }
    if (effectiveFtp) {
      return buildFallbackZones(effectiveFtp);
    }
    return null;
  })();

  const sourceLabel = isFromApi
    ? `[SYS] ${zoneSystem!.method.toUpperCase()}-DERIVED · FTP ${zoneSystem!.anchor_value ?? '—'}w`
    : effectiveFtp
      ? '[SYS] COGGAN 6-ZONE MODEL · FTP-DERIVED'
      : '[SYS] SET FTP IN TRAINING ENGINE TO UNLOCK';

  return (
    <div
      className={cn(
        'rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-4',
        'shadow-[inset_0_1px_0_rgba(0,200,255,0.04)]',
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1 h-1 rounded-full bg-[hsl(200,70%,55%)]" />
        <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground">
          POWER ZONE DISTRIBUTION
        </span>
        {effectiveFtp && (
          <span className="ml-auto text-[9px] font-mono tabular-nums text-[hsl(200,70%,55%)]">
            FTP {effectiveFtp}w
          </span>
        )}
        {isLoading && (
          <span className="ml-auto text-[8px] font-mono tracking-wider text-white/20 uppercase">LOADING</span>
        )}
      </div>

      {!zones ? (
        /* FTP not configured placeholder */
        <div className="py-6 flex flex-col items-center gap-2">
          <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-white/30">
            [SYS] FTP NOT CONFIGURED
          </span>
          <span className="text-[9px] font-mono text-white/20 tracking-wider text-center">
            Set your FTP in Training Engine to<br />enable power-based zones and TSS
          </span>
        </div>
      ) : (
        /* Zone bars */
        <div className="space-y-2.5">
          {zones.map((zone) => (
            <div key={zone.id} className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono tracking-wider text-[#A0A0A0]">
                  {zone.label}
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-mono tabular-nums text-white/40">
                    {zone.range}
                  </span>
                  <span className="text-[10px] font-mono tabular-nums text-foreground w-7 text-right">
                    {zone.pct}%
                  </span>
                </div>
              </div>
              <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${zone.pct}%`,
                    backgroundColor: zone.color,
                    boxShadow: `0 0 8px ${zone.color}40`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-white/[0.06]">
        <p className="text-[9px] font-mono text-white/25 tracking-wider text-center uppercase">
          {sourceLabel}
        </p>
      </div>
    </div>
  );
}
